// Service detail. Admins edit in place; members see the same fields
// read-only. Future chunks will add the escalation-policy attachment,
// integration list, and incident history sections — placeholders for
// those are left visible so the page shape doesn't shift dramatically
// each chunk.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { ArrowLeft, AlertCircle, Plug, Save, Trash2, Server, Workflow } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgServiceDoc, orgEscalationPoliciesCol } from '../lib/firestore';
import {
  STATUS_LABELS,
  STATUS_TONES,
  TIER_LABELS,
  TIER_TONES,
  deleteService,
  updateService,
} from '../lib/services';
import { readPolicy } from '../lib/escalations';
import type { EscalationPolicy, IncidentPriority, Service, ServiceTier } from '../lib/types';
import { TriggerIncidentDialog } from '../components/incidents/TriggerIncidentDialog';
import { IntegrationsCard } from '../components/integrations/IntegrationsCard';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function ServiceDetailPage() {
  const { id: serviceId } = useParams<{ id: string }>();
  const { orgId, role } = useOrg();
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  const [service, setService] = useState<Service | null | 'missing'>(null);
  const [policies, setPolicies] = useState<EscalationPolicy[]>([]);

  // The current pending selection (admin only). Saved separately from
  // name/tier/description because changes here apply via a dedicated
  // updateService call rather than the Save button.
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Local edit state — seeded from snapshot once and after each save.
  const [name, setName] = useState('');
  const [tier, setTier] = useState<ServiceTier>('tier3');
  const [description, setDescription] = useState('');
  const [seeded, setSeeded] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !serviceId) return;
    const unsub = onSnapshot(
      orgServiceDoc(orgId, serviceId),
      (snap) => {
        if (!snap.exists()) {
          setService('missing');
          return;
        }
        const data = snap.data() as Service;
        setService(data);
        if (!seeded) {
          setName(data.name);
          setTier(data.tier);
          setDescription(data.description ?? '');
          setSeeded(true);
        }
      },
      (err) => {
        console.error('[service] snapshot failed', err);
        setError('Could not load service.');
      },
    );
    return () => unsub();
  }, [orgId, serviceId, seeded]);

  // Load policies for the selector. Members can read the list (rules
  // were loosened in chunk 7b); admins use it to attach.
  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      query(orgEscalationPoliciesCol(orgId), orderBy('name', 'asc')),
      (snap) => {
        setPolicies(snap.docs.map((d) => readPolicy(d.data() as Parameters<typeof readPolicy>[0])));
      },
      (err) => {
        console.error('[service] policies snapshot failed', err);
      },
    );
    return () => unsub();
  }, [orgId]);

  if (!orgId || !serviceId) return null;

  if (service === null) {
    return (
      <AppShell>
        <Container>
          <Row gap={2} align="center">
            <Spinner /> <span className="ds-text-muted">Loading…</span>
          </Row>
        </Container>
      </AppShell>
    );
  }

  if (service === 'missing') {
    return (
      <AppShell>
        <Container>
          <Stack gap={4}>
            <ErrorBanner message="Service not found. It may have been deleted." />
            <Link to="/services" className="ds-button ds-button--secondary">
              <ArrowLeft size={14} strokeWidth={2} />
              Back to services
            </Link>
          </Stack>
        </Container>
      </AppShell>
    );
  }

  const dirty =
    name.trim() !== service.name ||
    tier !== service.tier ||
    description.trim() !== (service.description ?? '');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving || !dirty || service === 'missing' || service === null) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await updateService(orgId!, serviceId!, {
        name: name.trim(),
        tier,
        description: description.trim(),
      });
      setInfo('Saved.');
    } catch (err) {
      console.error('[service] update failed', err);
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteService(orgId!, serviceId!);
      navigate('/services', { replace: true });
    } catch (err) {
      console.error('[service] delete failed', err);
      setError(extractMessage(err));
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }

  return (
    <AppShell>
      <Container>
        <Stack gap={2}>
          <Link
            to="/services"
            className="ds-text-sm ds-text-muted"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ArrowLeft size={14} strokeWidth={2} />
            All services
          </Link>
        </Stack>

        <PageHeader
          title={service.name}
          actions={
            <Button
              variant="danger"
              onClick={() => setTriggerOpen(true)}
              iconLeft={<AlertCircle size={14} strokeWidth={2} />}
            >
              Trigger incident
            </Button>
          }
          subtitle={
            <Row gap={2} align="center">
              <Badge tone={TIER_TONES[service.tier]}>{TIER_LABELS[service.tier]}</Badge>
              <Badge tone={STATUS_TONES[service.currentStatus]} dot>
                {STATUS_LABELS[service.currentStatus]}
              </Badge>
            </Row>
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <ErrorBanner message={error} />
          </div>
        )}
        {info && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              background: 'var(--color-success-soft)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              color: 'var(--color-success-text)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {info}
          </div>
        )}

        <Stack gap={6}>
          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <Server size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Details
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <Stack gap={4} as="form" onSubmit={save}>
                <FormField label="Name">
                  {(id) => (
                    <Input
                      id={id}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      disabled={!isAdmin}
                    />
                  )}
                </FormField>
                <FormField label="Tier">
                  {(id) => (
                    <select
                      id={id}
                      className="ds-input"
                      value={tier}
                      onChange={(e) => setTier(e.target.value as ServiceTier)}
                      disabled={!isAdmin}
                    >
                      <option value="tier1">Tier 1 — Critical</option>
                      <option value="tier2">Tier 2 — Major</option>
                      <option value="tier3">Tier 3 — Minor</option>
                    </select>
                  )}
                </FormField>
                <FormField label="Description">
                  {(id) => (
                    <textarea
                      id={id}
                      className="ds-input"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={500}
                      rows={3}
                      disabled={!isAdmin}
                    />
                  )}
                </FormField>
                {isAdmin && (
                  <Row justify="end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!dirty}
                      loading={saving}
                      iconLeft={!saving ? <Save size={14} strokeWidth={2} /> : undefined}
                    >
                      Save changes
                    </Button>
                  </Row>
                )}
              </Stack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <Workflow size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Escalation policy
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <PolicyAttachment
                isAdmin={isAdmin}
                policies={policies}
                currentPolicyId={service.escalationPolicyId || ''}
                saving={savingPolicy}
                onChange={async (policyId) => {
                  if (savingPolicy) return;
                  setSavingPolicy(true);
                  setError(null);
                  setInfo(null);
                  try {
                    await updateService(orgId!, serviceId!, {
                      escalationPolicyId: policyId === '' ? null : policyId,
                    });
                    setInfo(policyId ? 'Policy attached.' : 'Policy detached.');
                  } catch (err) {
                    console.error('[service] attach policy failed', err);
                    setError(extractMessage(err));
                  } finally {
                    setSavingPolicy(false);
                  }
                }}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <Plug size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Integrations
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <IntegrationsCard orgId={orgId!} serviceId={serviceId!} isAdmin={isAdmin} />
            </CardBody>
          </Card>

          {isAdmin && (
            <Card>
              <CardHeader>
                <Row gap={2} align="center">
                  <h2
                    style={{
                      fontSize: 'var(--fs-md)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-danger-text)',
                    }}
                  >
                    Danger zone
                  </h2>
                </Row>
              </CardHeader>
              <CardBody>
                <Row gap={4} align="center" justify="between">
                  <Stack gap={1}>
                    <span className="ds-text-sm" style={{ fontWeight: 'var(--fw-medium)' }}>
                      Delete this service
                    </span>
                    <span className="ds-text-tertiary ds-text-xs">
                      Permanent. Any incidents history attached to this service would be orphaned.
                    </span>
                  </Stack>
                  <Button
                    variant="danger"
                    iconLeft={<Trash2 size={14} strokeWidth={2} />}
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={deleting}
                  >
                    Delete
                  </Button>
                </Row>
              </CardBody>
            </Card>
          )}
        </Stack>

        <ConfirmDialog
          open={confirmDeleteOpen}
          onConfirm={remove}
          onCancel={() => setConfirmDeleteOpen(false)}
          title={`Delete ${service.name}?`}
          confirmLabel="Delete service"
          destructive
          loading={deleting}
        >
          <p>
            This permanently removes the service from your org. Any incidents history attached to
            it would be orphaned.
          </p>
          <p>This cannot be undone.</p>
        </ConfirmDialog>

        <TriggerIncidentDialog
          orgId={orgId!}
          serviceId={serviceId!}
          serviceName={service.name}
          defaultPriority={(['tier1', 'tier2'].includes(service.tier)
            ? service.tier === 'tier1'
              ? 'P1'
              : 'P2'
            : 'P3') as IncidentPriority}
          open={triggerOpen}
          onClose={() => setTriggerOpen(false)}
        />
      </Container>
    </AppShell>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}

function PolicyAttachment({
  isAdmin,
  policies,
  currentPolicyId,
  saving,
  onChange,
}: {
  isAdmin: boolean;
  policies: EscalationPolicy[];
  currentPolicyId: string;
  saving: boolean;
  onChange: (policyId: string) => void;
}) {
  const current = policies.find((p) => p.id === currentPolicyId);

  if (!isAdmin) {
    return current ? (
      <Stack gap={2}>
        <Row gap={2} align="center">
          <Badge tone="accent" dot>
            {current.name}
          </Badge>
          <span className="ds-text-tertiary ds-text-sm">
            {current.steps.length} {current.steps.length === 1 ? 'step' : 'steps'}
          </span>
        </Row>
        <p className="ds-text-tertiary ds-text-xs">
          Ask an admin if you need to change which policy fires for this service.
        </p>
      </Stack>
    ) : (
      <p className="ds-text-muted ds-text-sm">No escalation policy attached.</p>
    );
  }

  if (policies.length === 0) {
    return (
      <Stack gap={3}>
        <p className="ds-text-muted ds-text-sm">
          No policies in your org yet. Create one before attaching it here.
        </p>
        <Link
          to="/escalation-policies"
          className="ds-button ds-button--secondary ds-button--sm"
          style={{ alignSelf: 'flex-start' }}
        >
          Manage policies
        </Link>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <FormField label="Policy">
        {(id) => (
          <select
            id={id}
            className="ds-input"
            value={currentPolicyId}
            onChange={(e) => onChange(e.target.value)}
            disabled={saving}
          >
            <option value="">— Not attached —</option>
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.steps.length} {p.steps.length === 1 ? 'step' : 'steps'})
              </option>
            ))}
          </select>
        )}
      </FormField>
      {current && (
        <Link
          to={`/escalation-policies/${current.id}`}
          className="ds-text-sm"
          style={{ alignSelf: 'flex-start' }}
        >
          Edit {current.name} →
        </Link>
      )}
    </Stack>
  );
}
